import { useCallback, useEffect, useRef } from "react";
import { useLatest } from "hooks/useLatest";
import { useSmartSurveyorOnboarding } from "../useSmartSurveyorOnboarding";
import type { UseReviewPageOnboarding } from "./types";

/**
 * Binds the onboarding tour to the review page: it builds the page-specific
 * side effects (switch tab, open the first category) and delegates run state,
 * persistence and analytics to `useSmartSurveyorOnboarding`.
 *
 * The `startReview` tour step is interactive — it spotlights the in-app
 * Start Review button and waits for the user to click it.
 *
 * Two coordinations with the app's traversal state:
 *  1. When traversal begins (false → true), the tour auto-advances past the
 *     `startReview` step.
 *  2. When the tour returns to the `startReview` step while traversal is
 *     active (back-navigation from a later step), traversal is stopped so
 *     the spotlighted button reverts to "Start Review" and the user can
 *     replay the interaction.
 */
export const useReviewPageOnboarding: UseReviewPageOnboarding = ({
  canEdit,
  canDelete,
  groupIds,
  selectedGroupLabel,
  hasDiagrams,
  interactionIdle,
  isTraversing,
  setRightTab,
  onSelectGroup,
  onStopTraversing,
}) => {
  const wasTraversingRef = useRef(isTraversing);
  const latestIsTraversing = useLatest(isTraversing);
  const latestOnStopTraversing = useLatest(onStopTraversing);

  const showElementsTab = useCallback(() => {
    setRightTab("plan");
  }, [setRightTab]);

  const revealFirstCategory = useCallback(() => {
    const firstGroupId = groupIds[0];
    if (firstGroupId && selectedGroupLabel !== firstGroupId) {
      onSelectGroup(firstGroupId);
    }
  }, [groupIds, selectedGroupLabel, onSelectGroup]);

  const handleStepEntered = useCallback(
    (stepId: string) => {
      if (stepId !== "startReview") return;
      if (!latestIsTraversing.current) return;
      latestOnStopTraversing.current();
    },
    [latestIsTraversing, latestOnStopTraversing],
  );

  const onboarding = useSmartSurveyorOnboarding({
    canEdit,
    canDelete,
    hasElements: groupIds.length > 0,
    hasDiagrams,
    interactionIdle,
    onShowElementsTab: showElementsTab,
    onRevealFirstCategory: revealFirstCategory,
    onStepEntered: handleStepEntered,
  });

  const { advanceFromStep, isTourActive } = onboarding;

  useEffect(() => {
    if (isTourActive && !wasTraversingRef.current && isTraversing) {
      advanceFromStep("startReview");
    }
    wasTraversingRef.current = isTraversing;
  }, [isTraversing, advanceFromStep, isTourActive]);

  return onboarding;
};
